"use client";

/**
 * Shared product-variant selector (sizes / options). Renders a radio group of
 * pills, each showing the variant label and its resolved price. Controlled:
 * the parent owns the selected id and the price/cart wiring. Used by the
 * `/products` quick-look drawer (`ProductDetail`) and the canonical
 * `/p/[id]` buy box (`ProductDetailAddToCart`).
 */

import { useRef, type KeyboardEvent } from "react";

import { formatPrice } from "@/lib/format-price";
import { resolveVariantPrice } from "@/lib/product-pricing";

// Re-exported so existing importers (`ProductDetail`, `ProductDetailAddToCart`,
// `ProductsClient`) can keep importing it from here; the canonical definition
// now lives in the framework-agnostic `lib/product-pricing`.
export { resolveVariantPrice };

export interface VariantOption {
  id: string;
  label: string;
  /** Absolute price for this variant; falls back to the product base price. */
  pricePence: number | null;
}

/**
 * A11Y-VAR1 — the WAI-ARIA radiogroup keyboard contract, hand-rolled.
 *
 * Every option carries `role="radio"` inside a `role="radiogroup"`, but each was
 * also a natural tab stop with no arrow-key handling: N options meant N tab
 * stops, and Left/Right/Up/Down did nothing. That is exactly the failure
 * CLAUDE.md cites as the reason the ADMIN uses `@radix-ui/react-toggle-group`
 * ("one tab stop + arrow-key roving focus + a correct pressed contract"), and it
 * was sitting on the customer purchase path — the only route to an enabled
 * Add-to-cart on a variant-priced product.
 *
 * `@radix-ui/react-radio-group` is NOT a declared dependency here (and adding one
 * for two button rows isn't worth the bundle on a Worker with a size cap), so the
 * three rules are implemented directly:
 *   1. ONE tab stop — the checked option, or the FIRST option when nothing is
 *      checked yet (per the APG radio-group pattern).
 *   2. Arrow keys move focus AND selection, wrapping; Home/End jump to the ends.
 *   3. `aria-checked` was already correct and is left alone.
 * Click/tap behaviour and the visuals are untouched.
 */
function useRovingRadioGroup(
  variants: VariantOption[],
  selectedId: string | null,
  onSelect: (id: string) => void
) {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectedIndex = variants.findIndex((v) => v.id === selectedId);
  // Nothing chosen yet → the group is still reachable, via its first option.
  const tabStopIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const moveTo = (index: number) => {
    const target = variants[index];
    if (!target) return;
    onSelect(target.id);
    buttonRefs.current[index]?.focus();
  };

  const handleKeyDown = (from: number) => (e: KeyboardEvent<HTMLButtonElement>) => {
    const n = variants.length;
    if (n === 0) return;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        moveTo((from + 1) % n);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        moveTo((from - 1 + n) % n);
        break;
      case "Home":
        e.preventDefault();
        moveTo(0);
        break;
      case "End":
        e.preventDefault();
        moveTo(n - 1);
        break;
      default:
        break;
    }
  };

  const registerRef = (index: number) => (el: HTMLButtonElement | null) => {
    buttonRefs.current[index] = el;
  };

  return { tabStopIndex, handleKeyDown, registerRef };
}

export function VariantSelector({
  variants,
  basePricePence,
  selectedId,
  onSelect,
  showPrice = true,
  label = "Options",
  layout = "pills",
  className,
}: {
  variants: VariantOption[];
  basePricePence: number | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Show each variant's price beside its label. */
  showPrice?: boolean;
  /** Group label rendered above the options. */
  label?: string;
  /**
   * "pills" (default) — compact wrapping pills, used on the wide `/p/[id]` buy
   * box. "stacked" — one prominent full-width option per row with a radio
   * indicator, used in the narrow quick-view modal so the choice stands out
   * (and every option label fits regardless of length).
   */
  layout?: "pills" | "stacked";
  className?: string;
}) {
  // Hooks must run before the empty-variant early return.
  const { tabStopIndex, handleKeyDown, registerRef } = useRovingRadioGroup(
    variants,
    selectedId,
    onSelect
  );

  if (variants.length === 0) return null;
  // When every variant resolves to the same price (e.g. all inherit the
  // product base price because no per-variant price was set in admin), the
  // per-pill price is redundant noise — show labels only.
  const resolvedPrices = variants.map((v) =>
    resolveVariantPrice(v, basePricePence)
  );
  const allSamePrice = resolvedPrices.every((p) => p === resolvedPrices[0]);
  const showPriceResolved = showPrice && !allSamePrice;

  if (layout === "stacked") {
    return (
      <div className={className}>
        <span className="label-overline text-foreground">
          {label}
          <span className="ml-0.5 text-destructive" aria-hidden="true">
            *
          </span>
        </span>
        <div
          role="radiogroup"
          aria-label={label}
          className="mt-2 flex flex-col gap-2"
        >
          {variants.map((v, idx) => {
            const selected = v.id === selectedId;
            const price = resolvedPrices[idx];
            return (
              <button
                key={v.id}
                ref={registerRef(idx)}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={idx === tabStopIndex ? 0 : -1}
                onKeyDown={handleKeyDown(idx)}
                onClick={() => onSelect(v.id)}
                className={`flex min-h-12 w-full items-center justify-between gap-3 rounded-lg border-2 px-4 py-2.5 text-left transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
                  selected
                    ? "border-primary bg-primary/5"
                    : "border-border bg-background hover:border-primary/50 hover:bg-muted/40"
                }`}
              >
                <span className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className={`flex size-4.5 shrink-0 items-center justify-center rounded-full border-2 ${
                      selected ? "border-primary" : "border-muted-foreground/40"
                    }`}
                  >
                    {selected && (
                      <span className="size-2.5 rounded-full bg-primary" />
                    )}
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {v.label}
                  </span>
                </span>
                {showPriceResolved && price != null && (
                  <span className="shrink-0 text-sm font-semibold text-foreground">
                    {formatPrice(price)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <span className="text-sm font-medium text-foreground">
        {label}
        <span className="ml-0.5 text-destructive" aria-hidden="true">
          *
        </span>
      </span>
      <div
        role="radiogroup"
        aria-label={label}
        className="mt-1.5 flex flex-wrap gap-1.5"
      >
        {variants.map((v, idx) => {
          const selected = v.id === selectedId;
          const price = resolvedPrices[idx];
          return (
            <button
              key={v.id}
              ref={registerRef(idx)}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={idx === tabStopIndex ? 0 : -1}
              onKeyDown={handleKeyDown(idx)}
              onClick={() => onSelect(v.id)}
              className={`inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 sm:min-h-0 sm:min-w-0 sm:text-sm ${
                selected
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground hover:bg-muted"
              }`}
            >
              <span>{v.label}</span>
              {showPriceResolved && price != null && (
                <span
                  className={
                    selected
                      ? "text-primary-foreground/80"
                      : "text-muted-foreground"
                  }
                >
                  {formatPrice(price)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
